// The signed-in user's own preferences (GET/PUT /api/preferences). Like the firm-wide switches they
// are read and saved online only (no outbox); the last answer is kept in memory per user, so the
// reminders page can show when done reminders are deleted (also offline, once it was read).
import { useEffect, useReducer } from 'react';
import type { AutoDeleteDoneNotesDays, UpdatePreferencesRequest, UserPreferences } from '../../../../shared/types';
import { plural } from '../../lib/format';
import { request } from '../../lib/http';

export const AUTO_DELETE_CHOICES: ReadonlyArray<{ value: AutoDeleteDoneNotesDays | null; label: string }> = [
  { value: null, label: 'Nikoli' },
  { value: 7, label: 'po 7 dneh' },
  { value: 30, label: 'po 30 dneh' },
  { value: 90, label: 'po 90 dneh' },
  { value: 365, label: 'po 1 letu' },
];

/** "po 30 dneh", "po 1 letu" */
export function autoDeleteLabel(days: AutoDeleteDoneNotesDays): string {
  return days === 365 ? 'po 1 letu' : `po ${days} dneh`;
}

const DAY_MS = 24 * 3600 * 1000;
/** Just ticked off: the whole period ("čez 30 dni", not "29" a moment later once the server's time arrives). */
const JUST_DONE_MS = 15 * 60 * 1000;

/**
 * "izbriše se čez 5 dni" for a done reminder: the whole days left, never more than there are (the
 * deletion cannot be undone; 1 day and 20 hours left is "čez 1 dan"), under a day "v manj kot enem
 * dnevu". Only in the first minutes after ticking off does it show the whole period. Without
 * `doneAt` (ticked off on this device, not sent yet) the period starts now; it never shows more
 * than the whole period (the phone's clock may be behind the server's). The server deletes at most
 * an hour late: "v kratkem" once it is due.
 */
export function deletesInText(doneAt: string | null | undefined, days: AutoDeleteDoneNotesDays, now: Date): string {
  const parsed = doneAt ? Date.parse(doneAt) : Number.NaN;
  const from = Number.isFinite(parsed) ? Math.min(parsed, now.getTime()) : now.getTime();
  const elapsed = now.getTime() - from;
  const left = days * DAY_MS - elapsed;
  if (left <= 0) return 'izbriše se v kratkem';
  if (left < DAY_MS) return 'izbriše se v manj kot enem dnevu';
  const whole = elapsed < JUST_DONE_MS ? days : Math.floor(left / DAY_MS);
  return `izbriše se čez ${plural(whole, ['dan', 'dneva', 'dni', 'dni'])}`;
}

const known = new Map<string, UserPreferences>();
const listeners = new Set<() => void>();

function remember(userId: string, prefs: UserPreferences): UserPreferences {
  known.set(userId, prefs);
  for (const listener of [...listeners]) listener();
  return prefs;
}

/** GET /api/preferences of the signed-in user `userId` (online only; throws ApiClientError). */
export async function loadPreferences(userId: string): Promise<UserPreferences> {
  return remember(userId, await request<UserPreferences>('/api/preferences'));
}

/** PUT /api/preferences (online only; throws ApiClientError, status 0 without a connection). */
export async function savePreferences(userId: string, patch: UpdatePreferencesRequest): Promise<UserPreferences> {
  return remember(userId, await request<UserPreferences>('/api/preferences', { method: 'PUT', body: patch }));
}

/** The preferences of `userId` as last read (read again on mount); null while not known. */
export function usePreferences(userId: string | null): UserPreferences | null {
  const [, changed] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    listeners.add(changed);
    return () => {
      listeners.delete(changed);
    };
  }, []);
  useEffect(() => {
    if (!userId) return;
    loadPreferences(userId).catch(() => {
      // Offline, or a server without preferences: the page shows no deletion hints then.
    });
  }, [userId]);
  return userId ? (known.get(userId) ?? null) : null;
}
